import { useMemo, useState } from 'react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { LegendPayload } from 'recharts';
import { StepIndex } from '../../../constants/question';
import { RESULT_COLORS } from '../../../constants/result-colors';
import useMobile from '../../../hooks/useMobile';
import { Checkbox, Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';

// レーダーチャートの軸ラベル。'.' 区切りで複数行に折り返して表示する（customTick 参照）。
const SUBJECTS_JA = [
  'Q1.口唇',
  'Q2.舌',
  'Q3.歯肉.粘膜',
  'Q4.唾液',
  'Q5.残存歯',
  'Q6.義歯',
  'Q7.口腔.清掃',
  'Q8.歯痛',
];
const SUBJECTS_EN = [
  'Q1.Lips',
  'Q2.Tongue',
  'Q3.Gums.mucosa',
  'Q4.Saliva',
  'Q5.Teeth',
  'Q6.Dentures',
  'Q7.Oral.hygiene',
  'Q8.Pain',
];

// 凡例の表示順（今回 → 前回 → 初回）。
// 凡例の並びは描画順に依存しないよう itemSorter で固定する（recharts v3 は Legend の payload 指定を受け付けない）。
// 描画は「今回」（塗りつぶし不透明）を最背面にするため 今回 → 前回 → 初回 の順に並べている。
const LEGEND_ORDER: { [dataKey: string]: number } = {
  thisScore: 0,
  latestScore: 1,
  firstScore: 2,
};
const legendItemSorter = (item: LegendPayload) =>
  LEGEND_ORDER[String(item.dataKey)] ?? 99;

export type Answers = {
  score: {
    0: number;
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
    6: number;
    7: number;
  };
  /** 各設問（0〜7）の項目別メモ（ライトプラン限定）。 */
  memos?: { [step: number]: string };
  evaluatedAt: string;
  /** 評価者名。過去データ・ゲスト評価では未設定。 */
  evaluatorName?: string;
};

type Props = {
  answers: Answers;
  oldAnswers: Answers[];
};
const baseData = [
  {
    subject: '',
    thisScore: 0,
    firstScore: 0,
    latestScore: 0,
    fullMark: 2,
  },
  {
    subject: '',
    thisScore: 0,
    firstScore: 0,
    latestScore: 0,
    fullMark: 2,
  },
  {
    subject: '',
    thisScore: 0,
    firstScore: 0,
    latestScore: 0,
    fullMark: 2,
  },
  {
    subject: '',
    thisScore: 0,
    firstScore: 0,
    latestScore: 0,
    fullMark: 2,
  },
  {
    subject: '',
    thisScore: 0,
    firstScore: 0,
    latestScore: 0,
    fullMark: 2,
  },
  {
    subject: '',
    thisScore: 0,
    firstScore: 0,
    latestScore: 0,
    fullMark: 2,
  },
  {
    subject: '',
    thisScore: 0,
    firstScore: 0,
    latestScore: 0,
    fullMark: 2,
  },
  {
    subject: '',
    thisScore: 0,
    firstScore: 0,
    latestScore: 0,
    fullMark: 2,
  },
];
export default function OhatResultGraph(props: Props) {
  const { answers, oldAnswers } = props;
  const { colSize } = useMobile();
  const { t, i18n } = useTranslation();
  const subjects = i18n.language?.startsWith('en') ? SUBJECTS_EN : SUBJECTS_JA;
  const [showFirstResult, setShowFirstResult] = useState(true);
  const [showLatestResult, setShowLatestResult] = useState(true);

  const allData = useMemo(() => {
    const [first, latest] = oldAnswers;
    // スコアが 0 の場合、0.3 に置き換える
    const toScore = (score: number) => (score === 0 ? 0.3 : score);
    // baseData はモジュール共有のため、要素を書き換えず常に新しいオブジェクトを作る
    return baseData.map((base, i) => {
      const answerIdx = i as StepIndex;
      return {
        ...base,
        subject: subjects[i],
        thisScore: toScore(answers.score[answerIdx]),
        firstScore: first ? toScore(first.score[answerIdx]) : base.firstScore,
        latestScore: latest
          ? toScore(latest.score[answerIdx])
          : base.latestScore,
      };
    });
  }, [answers, oldAnswers, subjects]);

  function customTick(tickProps: any) {
    const { payload, x, y, textAnchor, stroke } = tickProps;
    const textParts = payload.value.split('.');
    return (
      <g className="recharts-layer recharts-polar-angle-axis-tick">
        <text
          stroke={stroke}
          x={x}
          y={y}
          className="recharts-text recharts-polar-angle-axis-tick-value"
          textAnchor={textAnchor}
        >
          {textParts.map((part: any, index: any) => (
            <tspan
              x={x}
              dy={index !== 0 ? `1.2em` : part === 'Q1' ? '-12px' : 3} //  Q1だけ、グラフに文字がかかってしまうための対応
              key={`tspan-${index}`}
            >
              {part}
            </tspan>
          ))}
        </text>
      </g>
    );
  }

  if (!allData) return null;

  return (
    <Space.Compact
      orientation="vertical"
      style={{ width: '100%', paddingTop: 20 }}
    >
      <Checkbox.Group
        defaultValue={['first', 'latest']}
        style={{ width: '100%' }}
      >
        <Row justify={'center'}>
          {oldAnswers[1] && (
            <Checkbox
              value="latest"
              checked={showLatestResult}
              onChange={(e) => {
                setShowLatestResult(e.target.checked);
              }}
            >
              {t('ohat.showPrevious')}
            </Checkbox>
          )}
          {oldAnswers[0] && (
            <Checkbox
              value="first"
              checked={showFirstResult}
              onChange={(e) => {
                setShowFirstResult(e.target.checked);
              }}
            >
              {t('ohat.showFirst')}
            </Checkbox>
          )}
        </Row>
      </Checkbox.Group>
      <ResponsiveContainer
        width="100%"
        height={colSize === 'xs' ? 350 : colSize === 'sm' ? 400 : 500}
      >
        <RadarChart
          data={allData}
          margin={{
            top: 0,
            right: colSize === 'xs' ? 20 : 40,
            left: colSize === 'xs' ? 20 : 40,
            bottom: 0,
          }}
        >
          {(oldAnswers[0] || oldAnswers[1]) && (
            <Legend verticalAlign="top" itemSorter={legendItemSorter} />
          )}
          <PolarGrid />
          <PolarAngleAxis dataKey="subject" tick={customTick} />
          <PolarRadiusAxis
            angle={68}
            domain={[0, 2]}
            tickCount={3}
            tick={{ fill: RESULT_COLORS.chart.axisTick, fontSize: 12 }}
          />
          {/* 「今回」は塗りつぶしが不透明（fillOpacity=1）なので、後から描くと前回・初回の線を隠してしまう。
              そのため 今回 → 前回 → 初回 の順に並べ、今回を最背面、初回を最前面に描画する。 */}
          {/* PDFダウンロード時は幅が変わって再描画されるため、アニメーション途中
              （図形がまだ描かれていない状態）をキャプチャしないよう全Radarでアニメーションを切る。 */}
          <Radar
            name={t('ohat.thisTime')}
            dataKey="thisScore"
            isAnimationActive={false}
            legendType="line"
            stroke={RESULT_COLORS.chart.thisTime}
            fill={RESULT_COLORS.chart.thisTime}
            fillOpacity={1}
            strokeWidth={2}
          />
          {showLatestResult && oldAnswers[1] && (
            <Radar
              name={t('ohat.previous')}
              dataKey="latestScore"
              isAnimationActive={false}
              legendType="line"
              stroke={RESULT_COLORS.chart.previous}
              fill={RESULT_COLORS.chart.previous}
              fillOpacity={0}
              strokeWidth={3}
            />
          )}
          {showFirstResult && oldAnswers[0] && (
            <Radar
              name={t('ohat.first')}
              dataKey="firstScore"
              isAnimationActive={false}
              legendType="plainline"
              stroke={RESULT_COLORS.chart.first}
              fill={RESULT_COLORS.chart.first}
              fillOpacity={0}
              strokeWidth={3}
              strokeDasharray="3 3"
            />
          )}
        </RadarChart>
      </ResponsiveContainer>
    </Space.Compact>
  );
}
